import {
  useGlobal,
  useRoadMap,
  VojomoLoader,
  useAccount
} from "@vojomo-core/shared";
import { useEffect, useMemo, useState, useRef } from "react";
import { RoadmapBkg } from "../../../RoadmapViewer/components/PathToPeak";
import { useTopic } from "../../hooks/use-topics";
import { RoadmapAtAGlance } from "./RoadmapAtAGlance";
import { Switch } from "antd";

export const RoadmapContent = () => {
  const { roadmapViewMode, setRoadmapViewMode } = useTopic();
  const {
    windowWidth,
    isMobile,
    view,
    setView,
    buildingBlocksDict,
    getAllBuildingBlocks
  } = useGlobal();

  const {
    headlineImgUrl,
    getHeadlineImgUrl,
    getRoadmapDates,
    roadmapDates,
    activeBlock,
    setActiveBlock
  } = useRoadMap();

  const { companyName, isUpdatingCompany } = useAccount();
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!view) {
      setView("my");
    }
  }, [view]);

  useEffect(() => {
    if (!activeBlock) {
      setActiveBlock("Vision");
    }
  }, [activeBlock]);

  const [mission, setMission] = useState<string>(null);
  const [vision, setVision] = useState<string>(null);
  const [peakAmbition, setPeakAmbition] = useState<string>(null);
  const [peakObjectives, setPeakObjectives] = useState<string>(null);
  const [threeYearObjectives, setThreeYearObjectives] = useState<string>(null);
  const [oneYearObjectives, setOneYearObjectives] = useState<string>(null);
  const [qObjectives, setQObjectives] = useState<string>(null);

  const getAllRoadmapContent = async () => {
    setVision(buildingBlocksDict[view]["Vision"]["text"]);
    setMission(buildingBlocksDict[view]["Mission"]["text"]);
    setPeakAmbition(buildingBlocksDict[view]["Peak Ambition"]["text"]);
    setPeakObjectives(
      buildingBlocksDict[view]["Peak Ambition Objectives"]["text"]
    );
    setThreeYearObjectives(
      buildingBlocksDict[view]["3 Year Objectives"]["text"]
    );
    setOneYearObjectives(buildingBlocksDict[view]["1 Year Objectives"]["text"]);
    setQObjectives(buildingBlocksDict[view]["Quarterly Objectives"]["text"]);
    getHeadlineImgUrl(buildingBlocksDict[view]["Peak Ambition"]["_id"]);
  };

  const [imageWidth, setImageWidth] = useState("93%");

  useEffect(() => {
    setImageWidth(windowWidth <= 1600 ? "80%" : "93%");
  }, [windowWidth]);

  useEffect(() => {
    getRoadmapDates();
  }, [view, companyName]);

  useEffect(() => {
    getAllBuildingBlocks(true);
  }, [companyName]);

  useEffect(() => {
    if (buildingBlocksDict) {
      getAllRoadmapContent();
    }
  }, [buildingBlocksDict, view, companyName]);

  const shouldDisplayLoader = useMemo(() => {
    return isUpdatingCompany || !buildingBlocksDict;
  }, [isUpdatingCompany, buildingBlocksDict]);

  // Calculate scale - larger on mobile for scrollable view
  const scale = isMobile ? 0.6 : 0.525;
  const scaledWidth = 2136 * scale;
  const scaledHeight = 1151 * scale;

  return (
    <>
      {shouldDisplayLoader ? (
        <VojomoLoader />
      ) : (
        <div
          style={{
            width: "100%",
            height: "100%",
            display: "flex",
            flexDirection: "column"
          }}
        >
          {/* View Toggle Switch */}
          <div
            style={{
              padding: isMobile ? "10px 12px" : "12px 16px",
              backgroundColor: "white",
              borderBottom: "1px solid #E5E5E5",
              display: "flex",
              alignItems: "center",
              gap: isMobile ? "8px" : "12px"
            }}
          >
            <span
              style={{
                fontSize: isMobile ? "13px" : "14px",
                fontWeight: 500,
                color: roadmapViewMode === "visual" ? "#000" : "#7B7B7B"
              }}
            >
              {isMobile ? "Visual" : "Path to Peak"}
            </span>
            <Switch
              checked={roadmapViewMode === "table"}
              onChange={(checked) => {
                setRoadmapViewMode(checked ? "table" : "visual");
              }}
            />
            <span
              style={{
                fontSize: isMobile ? "13px" : "14px",
                fontWeight: 500,
                color: roadmapViewMode === "table" ? "#000" : "#7B7B7B"
              }}
            >
              {isMobile ? "Table" : "At a Glance"}
            </span>
          </div>

          {/* Content Area */}
          <div style={{
            flex: 1,
            overflow: "auto",
            WebkitOverflowScrolling: "touch",
            ...(isMobile && roadmapViewMode === "visual" ? {
              padding: "12px"
            } : {})
          }}>
            {roadmapViewMode === "table" ? (
              <RoadmapAtAGlance />
            ) : (
              <div
                ref={containerRef}
                style={{
                  width: `${scaledWidth}px`,
                  height: `${scaledHeight}px`,
                  position: "relative",
                  flexShrink: 0,
                  overflow: "hidden"
                }}
              >
                <div
                  style={{
                    transform: `scale(${scale})`,
                    transformOrigin: "top left",
                    width: "2136px",
                    height: "1151px",
                    position: "relative"
                  }}
                >
                  <RoadmapBkg
                    width={imageWidth}
                    peakAmbitionDate={
                      roadmapDates?.peak_ambition_date || "No Date Yet"
                    }
                    peakAmbitionContent={peakAmbition}
                    peakObjectiveDate={
                      roadmapDates?.peak_ambition_date || "No Date Yet"
                    }
                    peakObjectiveContent={peakObjectives}
                    mission={mission || "No mission yet..."}
                    vision={vision || "No vision yet..."}
                    threeYearObjectivesDate={roadmapDates?.three_year}
                    threeYearObjectivesContent={threeYearObjectives}
                    oneYearObjectivesDate={roadmapDates?.one_year}
                    oneYearObjectivesContent={oneYearObjectives}
                    qObjectivesDate={roadmapDates?.next_quarter}
                    qObjectivesContent={qObjectives}
                    headlineImgUrl={headlineImgUrl}
                    embedded={true}
                    readOnly={true}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};
